import { useMemo } from 'react';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { TaskFilterOptions } from 'shared/types/filter';
import { TaskType } from 'shared/types/task-base';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

import { useHabits } from '../use-habits';
import { processScheduleTasks } from './process-schedule-tasks';

export const useFilteredHabits = (
  filters: TaskFilterOptions | TaskFilterOptions[],
) => {
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const habits = useHabits();

  return useMemo(() => {
    const filtersArray = Array.isArray(filters) ? filters : [filters];

    const hasDeadlineDeadlineFilters = filtersArray.every(
      (f) =>
        f.deadlineDeadlineStartDate ||
        f.deadlineDeadlineEndDate ||
        f.hasDeadlineDeadline,
    );

    if (hasDeadlineDeadlineFilters) {
      return [];
    }

    // Check if ALL filter sets exclude habits. If even one allows habits, we proceed.
    const allowsHabits = filtersArray.some(
      (f) => !f.types?.length || f.types.includes(TaskType.Habit),
    );

    if (!allowsHabits) {
      return [];
    }

    // filter out the habits with `hideOnTodo` when those should be excluded
    const _habits = habits.filter((habit) => {
      if (habit.archivedAt || habit.frozenAt) {
        return false;
      }
      if (filtersArray.some((f) => f.excludeHiddenTodoHabits)) {
        return !habit.hideOnTodo;
      }
      return true;
    });

    return processScheduleTasks(_habits, filters, {
      weekStartsOn,
      today,
    });
  }, [filters, habits, today, weekStartsOn]);
};
